import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = (typeof d === 'string' ? d : '').split('T')[0].split('-').map(Number);
  return (y && m && day) ? new Date(y, m - 1, day).toLocaleDateString('fr-FR') : '—';
}

function formatMontant(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
  }).format(m);
}

function formatMontantInt(m) {
  if (m == null) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
  }).format(m);
}

export default function FacturesServices({ user }) {
  const [factures, setFactures]       = useState([]);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats]             = useState(null);
  const [loading, setLoading]         = useState(false);
  const [syncing, setSyncing]         = useState(false);
  const [syncProgress, setSyncProgress] = useState('');
  const [syncMsg, setSyncMsg]         = useState('');
  const [syncError, setSyncError]     = useState('');
  const pollRef                       = useRef(null);
  const [search, setSearch]           = useState('');
  const [filterDept, setFilterDept]   = useState('');
  const [dateDebut, setDateDebut]     = useState('');
  const [dateFin, setDateFin]         = useState('');
  const [selected, setSelected]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search)     params.search     = search;
      if (filterDept) params.department = filterDept;
      if (dateDebut)  params.date_debut = dateDebut;
      if (dateFin)    params.date_fin   = dateFin;

      const r = await api.get('/factures-services', { params });
      setFactures(r.data.factures || []);
    } catch (err) {
      console.error('Erreur chargement factures services:', err);
    } finally {
      setLoading(false);
    }
  }, [search, filterDept, dateDebut, dateFin]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/factures-services/departments')
      .then(r => setDepartments(r.data.departments || []))
      .catch(() => {});
    api.get('/factures-services/stats')
      .then(r => setStats(r.data.stats))
      .catch(() => {});
  }, []);

  // Arrêter le polling quand le composant se démonte
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncMsg('');
    setSyncError('');
    setSyncProgress('Connexion SFTP…');
    try {
      // Lancer la sync (réponse immédiate)
      await api.post('/factures-services/sync');

      // Poller /sync-status toutes les 3 secondes
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const s = await api.get('/factures-services/sync-status');
          const state = s.data;
          setSyncProgress(state.progress || '');

          if (!state.running) {
            stopPolling();
            setSyncing(false);
            setSyncProgress('');
            if (state.error) {
              setSyncError(state.error);
            } else {
              if (state.result) setSyncMsg(state.result.message || 'Synchronisation terminée');
              await load();
              const sr = await api.get('/factures-services/stats');
              setStats(sr.data.stats);
              const dr = await api.get('/factures-services/departments');
              setDepartments(dr.data.departments || []);
            }
          }
        } catch {
          // Erreur réseau passagère, on continue
        }
      }, 3000);

    } catch (err) {
      stopPolling();
      setSyncing(false);
      setSyncProgress('');
      setSyncError(err.response?.data?.error || 'Erreur lors du lancement de la synchronisation');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <div style={{ padding: '16px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
        <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#111827' }}>
          🧾 Factures Services
        </h2>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            padding: '7px 16px', background: syncing ? '#9ca3af' : '#0ea5e9',
            color: '#fff', border: 'none', borderRadius: '6px',
            fontSize: '13px', fontWeight: 600, cursor: syncing ? 'default' : 'pointer',
          }}
        >
          {syncing ? '⏳ Synchronisation…' : '🔄 Sync SFTP (Ateliers.all.csv)'}
        </button>
      </div>

      {/* Feedback sync */}
      {syncing && syncProgress && (
        <div style={{ padding: '10px 14px', background: '#eff6ff', color: '#1d4ed8', borderRadius: '6px', fontSize: '13px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
          {syncProgress}
          <span style={{ fontSize: '11px', color: '#3b82f6', marginLeft: 'auto' }}>Le fichier est volumineux (~220 Mo), la sync peut prendre plusieurs minutes…</span>
        </div>
      )}
      {syncMsg && (
        <div style={{ padding: '10px 14px', background: '#dcfce7', color: '#166534', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>
          ✅ {syncMsg}
        </div>
      )}
      {syncError && (
        <div style={{ padding: '10px 14px', background: '#fee2e2', color: '#b91c1c', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>
          ❌ {syncError}
        </div>
      )}

      {/* KPIs */}
      {stats && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {[
            { label: 'Total factures', value: parseInt(stats.total).toLocaleString('fr-FR'), color: '#3b82f6' },
            { label: 'Ce mois', value: parseInt(stats.ce_mois).toLocaleString('fr-FR'), color: '#8b5cf6' },
            { label: 'Montant total', value: formatMontantInt(stats.montant_total), color: '#059669' },
            { label: 'Montant ce mois', value: formatMontantInt(stats.montant_mois), color: '#f59e0b' },
          ].map(kpi => (
            <div key={kpi.label} style={{
              background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '12px 16px', minWidth: '140px', flex: 1,
            }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>{kpi.label}</div>
              <div style={{ fontSize: '18px', fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
            </div>
          ))}
          {stats.derniere_sync && (
            <div style={{
              background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px',
              padding: '12px 16px', minWidth: '160px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center',
            }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>Dernière sync</div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                {new Date(stats.derniere_sync).toLocaleString('fr-FR')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filtres */}
      <form onSubmit={handleSearch} style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <input
          type="text"
          placeholder="🔍 Facture, OR, client, immat…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: '200px', padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
        />
        <select
          value={filterDept}
          onChange={e => setFilterDept(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
        >
          <option value="">Tous les départements</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <input
          type="date" value={dateDebut}
          onChange={e => setDateDebut(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
        />
        <input
          type="date" value={dateFin}
          onChange={e => setDateFin(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
        />
        <button
          type="submit"
          style={{ padding: '7px 14px', background: '#374151', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
        >
          Filtrer
        </button>
        {(search || filterDept || dateDebut || dateFin) && (
          <button type="button" onClick={() => { setSearch(''); setFilterDept(''); setDateDebut(''); setDateFin(''); }}
            style={{ padding: '7px 10px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>
            ✕ Réinitialiser
          </button>
        )}
      </form>

      {/* Tableau */}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                {['N° Facture', 'Date facture', 'N° OR', 'Client', 'Immat', 'Marque', 'Département', 'Réceptionnaire', 'Montant HT', 'Lignes'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>Chargement…</td></tr>
              ) : factures.length === 0 ? (
                <tr><td colSpan={10} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                  Aucune facture — lancez une synchronisation SFTP
                </td></tr>
              ) : factures.map(f => (
                <tr
                  key={f.id}
                  onClick={() => setSelected(f)}
                  style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f9fafb'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '9px 12px', fontWeight: 600, color: '#1d4ed8', whiteSpace: 'nowrap' }}>{f.num_facture}</td>
                  <td style={{ padding: '9px 12px', whiteSpace: 'nowrap' }}>{formatDate(f.date_facture)}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#374151' }}>{f.num_or || '—'}</td>
                  <td style={{ padding: '9px 12px', maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.nom_client || '—'}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontSize: '12px' }}>{f.immat || '—'}</td>
                  <td style={{ padding: '9px 12px' }}>{f.marque || '—'}</td>
                  <td style={{ padding: '9px 12px', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{
                      background: '#eff6ff', color: '#1d4ed8',
                      padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                    }}>{f.department || '—'}</span>
                  </td>
                  <td style={{ padding: '9px 12px', color: '#6b7280' }}>{f.receptionnaire || '—'}</td>
                  <td style={{
                    padding: '9px 12px', fontWeight: 600, whiteSpace: 'nowrap',
                    color: parseFloat(f.montant_ht) < 0 ? '#dc2626' : '#059669',
                  }}>
                    {formatMontant(f.montant_ht)}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'center', color: '#6b7280' }}>{f.nb_lignes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {factures.length > 0 && (
          <div style={{ padding: '10px 14px', borderTop: '1px solid #e5e7eb', fontSize: '12px', color: '#6b7280' }}>
            {factures.length} facture(s) affichée(s)
            {factures.length === 500 && ' — affichage limité à 500 résultats, affinez vos filtres'}
          </div>
        )}
      </div>

      {/* Modal détail */}
      {selected && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setSelected(null)}
        >
          <div
            style={{ background: '#fff', borderRadius: '10px', width: '100%', maxWidth: '800px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
              <div>
                <span style={{ fontSize: '16px', fontWeight: 700, color: '#111827' }}>🧾 {selected.num_facture}</span>
                <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>
                  {formatDate(selected.date_facture)} — OR : {selected.num_or || '—'}
                </span>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            {/* Modal body */}
            <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
              {/* Infos générales */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Client', value: selected.nom_client },
                  { label: 'Immatriculation', value: selected.immat },
                  { label: 'VIN', value: selected.vin },
                  { label: 'Marque', value: selected.marque },
                  { label: 'Société', value: selected.societe },
                  { label: 'Département', value: selected.department },
                  { label: 'Réceptionnaire', value: selected.receptionnaire },
                  { label: 'Date ouverture OR', value: formatDate(selected.date_ouverture) },
                  { label: 'Date facture', value: formatDate(selected.date_facture) },
                  { label: 'Montant HT total', value: formatMontant(selected.montant_ht), highlight: true },
                ].map(item => (
                  <div key={item.label} style={{ background: '#f9fafb', borderRadius: '6px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '3px' }}>{item.label}</div>
                    <div style={{ fontWeight: item.highlight ? 700 : 500, color: item.highlight ? '#059669' : '#111827', fontSize: item.highlight ? '15px' : '13px' }}>
                      {item.value || '—'}
                    </div>
                  </div>
                ))}
              </div>

              {/* Lignes de facturation */}
              {selected.lignes_json && selected.lignes_json.length > 0 && (
                <>
                  <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '8px' }}>
                    Lignes ({selected.lignes_json.length})
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: '#f3f4f6' }}>
                        {['Prestation', 'Désignation', 'Réf.', 'Qté', 'Montant HT'].map(h => (
                          <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selected.lignes_json.map((l, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{
                              background: l.prestation === 'M.O.' ? '#fef3c7' : '#eff6ff',
                              color:      l.prestation === 'M.O.' ? '#92400e' : '#1d4ed8',
                              padding: '1px 6px', borderRadius: '4px', fontWeight: 600, fontSize: '11px',
                            }}>{l.prestation || '—'}</span>
                          </td>
                          <td style={{ padding: '6px 10px', color: '#374151' }}>{l.designation || '—'}</td>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: '#6b7280', fontSize: '11px' }}>{l.ref || '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>{l.qte != null ? l.qte : '—'}</td>
                          <td style={{
                            padding: '6px 10px', textAlign: 'right', fontWeight: 600,
                            color: parseFloat(l.montant_ht) < 0 ? '#dc2626' : '#059669',
                          }}>
                            {formatMontant(l.montant_ht)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                        <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, fontSize: '13px', textAlign: 'right' }}>Total HT</td>
                        <td style={{
                          padding: '8px 10px', fontWeight: 700, fontSize: '14px', textAlign: 'right',
                          color: parseFloat(selected.montant_ht) < 0 ? '#dc2626' : '#059669',
                        }}>
                          {formatMontant(selected.montant_ht)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
              <button
                onClick={() => setSelected(null)}
                style={{ padding: '8px 20px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
